import React from "react";
import { useDispatch } from "react-redux";
import { showErrorMessage } from "../actions";
import performRequest from "../configAxios";
import {
  EMPTY_WITHDRAWAL_FILTERS,
  TAB_DIALOG_FIELDS,
  TAB_HISTORY,
  TAB_PENDING,
  TAB_REDEMPTIONS,
  TAB_STATUS_MAP,
  TAB_WAITING_AUTH,
} from "../pages/components/HistoryWithdrawal";

const createInitialSummaryByTab = () => ({
  [TAB_PENDING]: { rows: [], totalCount: 0, summary: null },
  [TAB_HISTORY]: { rows: [], totalCount: 0, summary: null },
  [TAB_WAITING_AUTH]: { rows: [], totalCount: 0, summary: null },
  [TAB_REDEMPTIONS]: { rows: [], totalCount: 0, summary: null },
});

const createInitialFiltersByTab = () => ({
  [TAB_PENDING]: { ...EMPTY_WITHDRAWAL_FILTERS },
  [TAB_HISTORY]: { ...EMPTY_WITHDRAWAL_FILTERS },
  [TAB_WAITING_AUTH]: { ...EMPTY_WITHDRAWAL_FILTERS },
  [TAB_REDEMPTIONS]: { ...EMPTY_WITHDRAWAL_FILTERS },
});

const createInitialSortByTab = () => ({
  [TAB_PENDING]: { field: "", direction: "DESC" },
  [TAB_HISTORY]: { field: "", direction: "DESC" },
  [TAB_WAITING_AUTH]: { field: "", direction: "DESC" },
  [TAB_REDEMPTIONS]: { field: "", direction: "DESC" },
});

const clearInputFilterFields = (filters) => ({
  ...filters,
  withdrawId: "",
  maxAmount: "",
  nameOrEmail: "",
  userId: "",
  paymentId: "",
  contractId: "",
  maxValue: "",
});

const toEmptyTableResult = (page = 0) => ({
  data: [],
  page,
  totalCount: 0,
});

const addParam = (searchParams, key, value) => {
  if (value === undefined || value === null || value === "") return;

  if (Array.isArray(value)) {
    value.filter((item) => item !== undefined && item !== null && item !== "").forEach((item) => searchParams.append(key, item));
    return;
  }

  searchParams.append(key, value);
};

const REDEMPTION_DEFAULT_SORT = "paymentDate,DESC";

const normalizeSortDirection = (value) => {
  const parsed = String(value || "DESC").toUpperCase();
  return parsed === "ASC" ? "ASC" : "DESC";
};

const buildSortParam = (query, tab, currentSort) => {
  const orderField = query?.orderBy?.field || currentSort?.field;
  if (!orderField) {
    return tab === TAB_REDEMPTIONS ? REDEMPTION_DEFAULT_SORT : "";
  }

  const orderDirection = normalizeSortDirection(query?.orderDirection || currentSort?.direction);
  return `${orderField},${orderDirection}`;
};

const toNumericOrEmpty = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const parsed = Number(value);
  return Number.isNaN(parsed) ? "" : parsed;
};

const toUpperTextOrEmpty = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  return String(value).trim().toUpperCase();
};

const getPageContainerFromBodyData = (data) => {
  if (!data) return { pageData: null, summary: null };
  if (data.payments) return { pageData: data.payments, summary: data.summary || null };
  if (data.withdraws) return { pageData: data.withdraws, summary: data.summary || null };
  return { pageData: data, summary: data.summary || null };
};

const getBodyData = (response) => {
  const body = response?.data;
  if (!body) return null;

  // Supports both contracts: direct payload in response.data or wrapped in ApiResponse.data.
  return body?.data ?? body;
};

const useHistoryWithdrawalFilters = ({ currentUserRole, currentUserTaxResidence, initialTab = TAB_PENDING }) => {
  const dispatch = useDispatch();

  const [tabIndex, setTabIndex] = React.useState(initialTab);
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [pageSize, setPageSize] = React.useState(10);
  const [summaryByTab, setSummaryByTab] = React.useState(createInitialSummaryByTab);
  const [filtersByTab, setFiltersByTab] = React.useState(createInitialFiltersByTab);
  const [sortByTab, setSortByTab] = React.useState(createInitialSortByTab);

  const tableRef = React.useRef(null);

  const activeFilters = filtersByTab[tabIndex] || EMPTY_WITHDRAWAL_FILTERS;
  const filterTaxResidence = activeFilters.taxResidence || "";

  const reloadTable = React.useCallback((resetPage = false) => {
    if (!tableRef.current?.onQueryChange) return;

    const currentQuery = tableRef.current.state.query;
    tableRef.current.onQueryChange({
      ...currentQuery,
      page: resetPage ? 0 : currentQuery.page,
      pageSize: currentQuery.pageSize || pageSize,
    });
  }, [pageSize]);

  const updateFilter = React.useCallback((field, value) => {
    setFiltersByTab((prevState) => ({
      ...prevState,
      [tabIndex]: {
        ...prevState[tabIndex],
        [field]: value,
      },
    }));
  }, [tabIndex]);

  const applyFilters = React.useCallback(() => {
    reloadTable(true);
  }, [reloadTable]);

  const handleDebouncedFilterField = React.useCallback((name, value) => {
    updateFilter(name, value);
    reloadTable(true);
  }, [reloadTable, updateFilter]);

  const getDialogFields = React.useCallback(() => TAB_DIALOG_FIELDS[tabIndex] || [], [tabIndex]);

  const clearInputFilters = React.useCallback((targetTab = tabIndex) => {
    setFiltersByTab((prevState) => ({
      ...prevState,
      [targetTab]: clearInputFilterFields(prevState[targetTab] || EMPTY_WITHDRAWAL_FILTERS),
    }));
  }, [tabIndex]);

  const handleOrderChange = React.useCallback(
    (field, direction) => {
      if (!field) return;

      setSortByTab((prevState) => ({
        ...prevState,
        [tabIndex]: {
          field,
          direction: normalizeSortDirection(direction),
        },
      }));
    },
    [tabIndex],
  );

  const updateSummary = React.useCallback((tab, rows, totalCount, summary = null) => {
    setSummaryByTab((prevState) => ({
      ...prevState,
      [tab]: {
        rows,
        totalCount,
        summary,
      },
    }));
  }, []);

  const buildWithdrawSearchUrl = React.useCallback((query, tab) => {
    const filters = filtersByTab[tab] || EMPTY_WITHDRAWAL_FILTERS;
    const searchParams = new URLSearchParams();

    addParam(searchParams, "page", query.page ?? 0);
    addParam(searchParams, "size", query.pageSize ?? pageSize);

    const sort = buildSortParam(query, tab, sortByTab[tab]);
    if (sort) addParam(searchParams, "sort", sort);

    addParam(searchParams, "nameOrEmail", filters.nameOrEmail);
    addParam(searchParams, "userId", toNumericOrEmpty(filters.userId));
    addParam(searchParams, "withdrawId", toNumericOrEmpty(filters.withdrawId));
    addParam(searchParams, "maxAmount", toNumericOrEmpty(filters.maxAmount));
    addParam(searchParams, "partnerWithdraw", toNumericOrEmpty(filters.partnerWithdraw));
    addParam(searchParams, "taxResidence", filters.taxResidence);
    addParam(searchParams, "dateFilter", filters.dateFilter);

    const tabDefaultStatus = TAB_STATUS_MAP[tab] || [];
    const status = filters.status ? [filters.status] : tabDefaultStatus;
    addParam(searchParams, "status", status);

    return `/withdraw/history-withdrawal/search?${searchParams.toString()}`;
  }, [filtersByTab, pageSize, sortByTab]);

  const buildPaymentSearchUrl = React.useCallback((query, tab) => {
    const filters = filtersByTab[tab] || EMPTY_WITHDRAWAL_FILTERS;
    const searchParams = new URLSearchParams();

    addParam(searchParams, "page", query.page ?? 0);
    addParam(searchParams, "size", query.pageSize ?? pageSize);

    const sort = buildSortParam(query, tab, sortByTab[tab]);
    if (sort) addParam(searchParams, "sort", sort);

    addParam(searchParams, "nameOrEmail", filters.nameOrEmail);
    addParam(searchParams, "userId", toNumericOrEmpty(filters.userId));
    const paymentIdFilter = toNumericOrEmpty(filters.paymentId || filters.withdrawId);
    const contractIdFilter = toNumericOrEmpty(filters.contractId);
    addParam(searchParams, "paymentId", paymentIdFilter);
    addParam(searchParams, "contractId", contractIdFilter);
    addParam(searchParams, "maxValue", toNumericOrEmpty(filters.maxValue || filters.maxAmount));
    addParam(searchParams, "contractStatusRedeemPaid", toUpperTextOrEmpty(filters.contractStatusRedeemPaid) || "PAYMENT_GENERATED");
    addParam(searchParams, "paymentType", toUpperTextOrEmpty(filters.paymentType) || "REDEMPTION");
    addParam(searchParams, "userTaxResidence", filters.userTaxResidence || filters.taxResidence);

    return `/payment/history-withdrawal/search?${searchParams.toString()}`;
  }, [filtersByTab, pageSize, sortByTab]);

  const fetchTableData = React.useCallback(async (query) => {
    setPageSize(query.pageSize);

    try {
      const requestUrl = tabIndex === TAB_REDEMPTIONS ? buildPaymentSearchUrl(query, tabIndex) : buildWithdrawSearchUrl(query, tabIndex);
      const response = await performRequest("GET", requestUrl, null, true);
      const bodyData = getBodyData(response);
      const { pageData, summary } = getPageContainerFromBodyData(bodyData);

      const rows = pageData?.content || [];
      const totalElements = pageData?.totalElements ?? 0;
      const responsePage = pageData?.number ?? query.page;

      if (currentUserRole !== "ADMIN" && currentUserRole !== "FINANCE_MANAGER") {
        const restrictedRows = rows.filter((row) => {
          const taxResidence = row?.userTaxResidence ?? row?.account?.user?.taxResidence;
          return taxResidence === currentUserTaxResidence;
        });
        updateSummary(tabIndex, restrictedRows, totalElements, summary);

        return {
          data: restrictedRows,
          page: responsePage,
          totalCount: totalElements,
        };
      }

      updateSummary(tabIndex, rows, totalElements, summary);

      return {
        data: rows,
        page: responsePage,
        totalCount: totalElements,
      };
    } catch (error) {
      const status = error?.response?.status;

      if (status === 400 || status === 422) {
        dispatch(showErrorMessage(error?.response?.data?.message || "app.server.connect.error"));
      }

      if (status === 401 || status === 400 || status === 422) {
        updateSummary(tabIndex, [], 0, null);
        return toEmptyTableResult(query.page);
      }

      updateSummary(tabIndex, [], 0, null);
      return toEmptyTableResult(query.page);
    } finally {
      setIsInitialLoading((previous) => (previous ? false : previous));
    }
  }, [buildPaymentSearchUrl, buildWithdrawSearchUrl, currentUserRole, currentUserTaxResidence, dispatch, tabIndex, updateSummary]);

  return {
    tabIndex,
    setTabIndex,
    isInitialLoading,
    pageSize,
    filterTaxResidence,
    tableRef,
    activeFilters,
    summaryByTab,
    getDialogFields,
    updateFilter,
    applyFilters,
    reloadTable,
    fetchTableData,
    handleDebouncedFilterField,
    handleOrderChange,
    clearInputFilters,
  };
};

export default useHistoryWithdrawalFilters;
